import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, query, orderBy, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase-config';

export default function MarketingAutomation() {
  const [campaigns, setCampaigns] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    type: 'email',
    subject: '',
    content: '',
    targetSegment: 'all',
    scheduledDate: '',
    status: 'draft'
  });

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      const campaignsQuery = query(collection(db, 'campaigns'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(campaignsQuery);
      const campaignsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));
      setCampaigns(campaignsData);
    } catch (error) {
      console.error('Error loading campaigns:', error);
    }
  };

  const createCampaign = async () => {
    setLoading(true);
    try {
      await addDoc(collection(db, 'campaigns'), {
        ...newCampaign,
        createdAt: new Date(),
        updatedAt: new Date(),
        stats: {
          sent: 0,
          opened: 0,
          clicked: 0,
          converted: 0
        }
      });
      
      setNewCampaign({
        name: '',
        type: 'email',
        subject: '',
        content: '',
        targetSegment: 'all',
        scheduledDate: '',
        status: 'draft'
      });
      setShowCreateModal(false);
      loadCampaigns();
    } catch (error) {
      console.error('Error creating campaign:', error);
    }
    setLoading(false);
  };

  const generateAIContent = async (type) => {
    const templates = {
      mobilhaz: {
        subject: "🏠 Álmai mobilháza vár Önre - Exkluzív ajánlat!",
        content: `Kedves {name},

Szeretnénk bemutatni Önnek legújabb mobilház kollekcióinkat, amelyek tökéletesen ötvözik a modern kényelmet és a megfizethető árakat.

🌟 Miért válassza a HoppHomes mobilházakat?
• Prémium minőségű anyagok
• Energiahatékony megoldások
• Gyors telepítés (2-3 hét)
• 10 év garancia

💰 Különleges ajánlat: 15% kedvezmény az első 10 vásárlónak!

Kérjen ingyenes árajánlatot még ma: {cta_link}

Üdvözlettel,
HoppHomes Csapat`
      },
      ingatlan: {
        subject: "🏡 Új ingatlan lehetőségek - Személyre szabott ajánlatok",
        content: `Kedves {name},

Az Ön érdeklődési köre alapján kiválogattuk a legfrissebb ingatlan lehetőségeket.

🎯 Az Ön számára releváns ajánlatok:
• Modern családi házak
• Befektetési lehetőségek
• Építési telkek

📈 Piaci trend: Az ingatlanárak várhatóan 8-12%-kal emelkednek idén.

Tekintse meg személyre szabott ajánlatainkat: {cta_link}

Üdvözlettel,
HoppHomes Ingatlan Csapat`
      },
      auto: {
        subject: "🚗 Álmai autója megtalálható - AI-alapú ajánlások",
        content: `Kedves {name},

AI rendszerünk az Ön preferenciái alapján kiválogatta a legmegfelelőbb járműveket.

🔍 Személyre szabott ajánlatok:
• Megfelelő ár-érték arány
• Alacsony futásteljesítmény
• Teljes szervizkönyv
• Garancia

💡 AI tipp: A kiválasztott modellek értékállósága kiváló!

Böngéssze ajánlatainkat: {cta_link}

Üdvözlettel,
HoppHomes Autó Csapat`
      }
    };

    const template = templates[type] || templates.mobilhaz;
    setNewCampaign(prev => ({
      ...prev,
      subject: template.subject,
      content: template.content
    }));
  };

  const launchCampaign = async (campaignId) => {
    try {
      await updateDoc(doc(db, 'campaigns', campaignId), {
        status: 'active',
        launchedAt: new Date()
      });
      loadCampaigns();
    } catch (error) {
      console.error('Error launching campaign:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-500';
      case 'active': return 'bg-green-500';
      case 'paused': return 'bg-yellow-500';
      case 'completed': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'draft': return 'Tervezet';
      case 'active': return 'Aktív';
      case 'paused': return 'Szüneteltetve';
      case 'completed': return 'Befejezett';
      default: return 'Ismeretlen';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white flex items-center">
          <svg className="w-6 h-6 mr-2 text-[#e31937]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          Marketing Automatizáció
        </h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-[#e31937] hover:bg-[#b91c1c] text-white px-4 py-2 rounded-lg flex items-center"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Új Kampány
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-[#1a1a1a] rounded-xl p-4 border border-[#e31937]/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Aktív kampányok</p>
              <p className="text-white text-2xl font-bold">{campaigns.filter(c => c.status === 'active').length}</p>
            </div>
            <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-[#1a1a1a] rounded-xl p-4 border border-[#e31937]/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Email küldések</p>
              <p className="text-white text-2xl font-bold">2,547</p>
              <p className="text-green-400 text-xs">+12% ez a hét</p>
            </div>
            <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-[#1a1a1a] rounded-xl p-4 border border-[#e31937]/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Megnyitási arány</p>
              <p className="text-white text-2xl font-bold">24.8%</p>
              <p className="text-green-400 text-xs">+3.2% javulás</p>
            </div>
            <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-[#1a1a1a] rounded-xl p-4 border border-[#e31937]/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Konverziók</p>
              <p className="text-white text-2xl font-bold">187</p>
              <p className="text-green-400 text-xs">7.3% arány</p>
            </div>
            <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* AI Content Generator */}
      <div className="bg-[#1a1a1a] rounded-xl p-6 border border-[#e31937]/20">
        <h3 className="text-white font-semibold mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2 text-[#e31937]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          AI Tartalom Generátor
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => generateAIContent('mobilhaz')}
            className="bg-[#2a2a2a] hover:bg-[#3a3a3a] border border-gray-600 rounded-lg p-4 text-left transition-colors"
          >
            <div className="flex items-center mb-2">
              <span className="text-2xl mr-2">🏠</span>
              <span className="text-white font-medium">Mobilház Kampány</span>
            </div>
            <p className="text-gray-400 text-sm">AI-generált mobilház hirdetés személyre szabott tartalommal</p>
          </button>

          <button
            onClick={() => generateAIContent('ingatlan')}
            className="bg-[#2a2a2a] hover:bg-[#3a3a3a] border border-gray-600 rounded-lg p-4 text-left transition-colors"
          >
            <div className="flex items-center mb-2">
              <span className="text-2xl mr-2">🏡</span>
              <span className="text-white font-medium">Ingatlan Kampány</span>
            </div>
            <p className="text-gray-400 text-sm">Személyre szabott ingatlan ajánlatok AI segítségével</p>
          </button>

          <button
            onClick={() => generateAIContent('auto')}
            className="bg-[#2a2a2a] hover:bg-[#3a3a3a] border border-gray-600 rounded-lg p-4 text-left transition-colors"
          >
            <div className="flex items-center mb-2">
              <span className="text-2xl mr-2">🚗</span>
              <span className="text-white font-medium">Autó Kampány</span>
            </div>
            <p className="text-gray-400 text-sm">AI-alapú autó ajánlások és hirdetések</p>
          </button>
        </div>
      </div>

      {/* Campaigns List */}
      <div className="bg-[#1a1a1a] rounded-xl overflow-hidden border border-[#e31937]/20">
        <div className="overflow-x-auto">
          <table className="w-full text-white">
            <thead className="bg-[#e31937]">
              <tr>
                <th className="px-4 py-3 text-left">Kampány név</th>
                <th className="px-4 py-3 text-left">Típus</th>
                <th className="px-4 py-3 text-left">Státusz</th>
                <th className="px-4 py-3 text-left">Célcsoport</th>
                <th className="px-4 py-3 text-left">Teljesítmény</th>
                <th className="px-4 py-3 text-left">Műveletek</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map(campaign => (
                <tr key={campaign.id} className="border-b border-gray-700 hover:bg-[#2a2a2a]">
                  <td className="px-4 py-3 font-medium">{campaign.name}</td>
                  <td className="px-4 py-3">
                    <span className="capitalize">{campaign.type}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(campaign.status)} text-white`}>
                      {getStatusText(campaign.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3">{campaign.targetSegment}</td>
                  <td className="px-4 py-3">
                    <div className="text-sm">
                      <div>Küldve: {campaign.stats?.sent || 0}</div>
                      <div>Megnyitva: {campaign.stats?.opened || 0}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex space-x-2">
                      {campaign.status === 'draft' && (
                        <button
                          onClick={() => launchCampaign(campaign.id)}
                          className="bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded text-xs"
                        >
                          Indítás
                        </button>
                      )}
                      <button
                        onClick={() => setSelectedCampaign(campaign)}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-xs"
                      >
                        Részletek
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Campaign Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#1a1a1a] rounded-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">Új Marketing Kampány</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-gray-400 text-sm mb-2">Kampány neve</label>
                <input
                  type="text"
                  value={newCampaign.name}
                  onChange={(e) => setNewCampaign(prev => ({...prev, name: e.target.value}))}
                  className="w-full bg-[#2a2a2a] border border-gray-600 rounded-lg px-3 py-2 text-white"
                  placeholder="pl. Tavaszi Mobilház Akció"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Típus</label>
                  <select
                    value={newCampaign.type}
                    onChange={(e) => setNewCampaign(prev => ({...prev, type: e.target.value}))}
                    className="w-full bg-[#2a2a2a] border border-gray-600 rounded-lg px-3 py-2 text-white"
                  >
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                    <option value="push">Push értesítés</option>
                    <option value="whatsapp">WhatsApp</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-2">Célcsoport</label>
                  <select
                    value={newCampaign.targetSegment}
                    onChange={(e) => setNewCampaign(prev => ({...prev, targetSegment: e.target.value}))}
                    className="w-full bg-[#2a2a2a] border border-gray-600 rounded-lg px-3 py-2 text-white"
                  >
                    <option value="all">Minden felhasználó</option>
                    <option value="new_leads">Új lead-ek</option>
                    <option value="active_users">Aktív felhasználók</option>
                    <option value="mobile_home_interested">Mobilház érdeklődők</option>
                    <option value="real_estate_interested">Ingatlan érdeklődők</option>
                    <option value="car_interested">Autó érdeklődők</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">Tárgy (Email esetén)</label>
                <input
                  type="text"
                  value={newCampaign.subject}
                  onChange={(e) => setNewCampaign(prev => ({...prev, subject: e.target.value}))}
                  className="w-full bg-[#2a2a2a] border border-gray-600 rounded-lg px-3 py-2 text-white"
                  placeholder="Email tárgy"
                />
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">Tartalom</label>
                <textarea
                  value={newCampaign.content}
                  onChange={(e) => setNewCampaign(prev => ({...prev, content: e.target.value}))}
                  rows={8}
                  className="w-full bg-[#2a2a2a] border border-gray-600 rounded-lg px-3 py-2 text-white"
                  placeholder="Kampány tartalma..."
                />
              </div>

              <div className="flex space-x-2 pt-4">
                <button
                  onClick={createCampaign}
                  disabled={loading || !newCampaign.name || !newCampaign.content}
                  className="bg-[#e31937] hover:bg-[#b91c1c] disabled:opacity-50 text-white px-4 py-2 rounded-lg"
                >
                  {loading ? 'Létrehozás...' : 'Kampány Létrehozása'}
                </button>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg"
                >
                  Mégse
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
